import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/** @type {Map<string, Promise<string>>} */
const hydrationFetches = new Map();

/**
 * Haalt de verse sectie op zonder de zichtbare DOM al te vervangen.
 *
 * De header heeft de volledige menustructuur pas in de Section Rendering-
 * response. Een directe morph vanuit requestIdleCallback tekende de header
 * rond 2,6 s opnieuw en schoof daardoor de LCP van de hero naar dat moment.
 * Voorladen houdt de data klaar; morphen gebeurt pas bij echte interactie.
 *
 * @param {string} sectionId
 * @param {URL} [url]
 * @returns {Promise<string>}
 */
function prefetchSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const existingFetch = hydrationFetches.get(normalizedId);
  if (existingFetch) return existingFetch;

  const sectionFetch = sectionRenderer.getSectionHTML(normalizedId, false, url).catch((error) => {
    hydrationFetches.delete(normalizedId);
    throw error;
  });

  hydrationFetches.set(normalizedId, sectionFetch);
  return sectionFetch;
}

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  await prefetchSection(normalizedId, url);
  await sectionRenderer.renderSection(normalizedId, { cache: true, url, mode: 'hydration' });

  section.dataset.hydrated = 'true';
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  onDocumentReady(() => {
    const normalizedId = normalizeSectionId(sectionId);
    const section = document.getElementById(buildSectionSelector(normalizedId));
    if (!section) return;

    let hydrationPromise = null;
    const interactionEvents = ['pointerdown', 'focusin', 'keydown'];

    const removeInteractionListeners = () => {
      for (const eventName of interactionEvents) section.removeEventListener(eventName, hydrateOnInteraction);
    };

    const hydrateOnInteraction = () => {
      if (section.dataset.hydrated === 'true') {
        removeInteractionListeners();
        return;
      }

      hydrationPromise ??= hydrateSection(normalizedId, url)
        .then(removeInteractionListeners)
        .catch((error) => {
          hydrationPromise = null;
          console.error(`Section hydration failed for ${normalizedId}`, error);
        });
    };

    for (const eventName of interactionEvents) section.addEventListener(eventName, hydrateOnInteraction);

    requestIdleCallback(() => {
      prefetchSection(normalizedId, url).catch(() => {
        // Een interactie probeert de request opnieuw; zichtbare content blijft
        // intussen bruikbaar doordat de server de basisheader al heeft gezet.
      });
    });
  });
}
